import { isNaN } from 'lodash';
import dayjs from 'utils/dayjs';
import { treatAsUTC } from 'utils/helpers';
import { IDocumentFormFields } from 'types/documents/IDocumentFormFields';
import { DocumentCategories } from 'constants/DocumentCategories';
import { Document, DocumentToSend } from './types';

export const fromDocumentToDocumentToSend = (documents: Document[]): DocumentToSend[] => {
  const formattedModels: DocumentToSend[] = [];
  documents.forEach(document => {
    formattedModels.push({
      file: document.file,
      category: document.type.toString(),
      periodStart: dayjs(new Date(document.periodStart)).format('DD/MM/YYYY'),
      periodEnd: dayjs(new Date(document.periodEnd)).format('DD/MM/YYYY'),
      employeeId: document.employeeId ? document.employeeId.toString() : '',
    });
  });
  return formattedModels;
};

export const fromDocumentFormToDocumentToSend = (documents: IDocumentFormFields[]): DocumentToSend[] => {
  const formattedModels: DocumentToSend[] = [];
  documents.forEach(document => {
    formattedModels.push({
      ...document,
      category: document.type.toString(),
      periodStart: dayjs(new Date(document.periodStart)).format('DD/MM/YYYY'),
      periodEnd: dayjs(new Date(document.periodEnd)).format('DD/MM/YYYY'),
      employeeId: document.employeeId ? document.employeeId.toString() : '',
    });
  });
  return formattedModels;
};

export const formatDateToString = (date: Date) => {
  return dayjs(date).format('DD/MM/YYYY');
};

export const isDateValid = (d: any) => {
  return !isNaN(Date.parse(d));
};

export const doesNotNeedPeriod = (type: number): boolean => {
  const categoriesWithoutPeriod = [DocumentCategories.INTENTION_DECLARATION, DocumentCategories.CV];
  return categoriesWithoutPeriod.map(categoryId => Number(categoryId)).includes(type);
};

export const needsEmployeeDropdown = (type: number): boolean => {
  const categoriesWithEmployeeDropdown = [
    DocumentCategories.CONTRACT,
    DocumentCategories.ID,
    DocumentCategories.WORK_POST_FILE,
    DocumentCategories.STUDENT_INSCRIPTION_PROOF,
    DocumentCategories.VCA,
    DocumentCategories.MEDICAL_CHECK,
    DocumentCategories.SAFETY_PASSPORT,
    DocumentCategories.BANK_CARD,
    DocumentCategories.DRIVING_LICENCE,
    DocumentCategories.WORK_PERMIT,
    DocumentCategories.SOCIAL_DOCUMENT,
    DocumentCategories.INDIVIDUAL_ACCOUNT,
    DocumentCategories.VACATION_CERTIFICATE,
    DocumentCategories.EMPLOYMENT_CERTIFICATE,
    DocumentCategories.PAYSLIP,
    DocumentCategories.FISCAL_FILE,
    DocumentCategories.INTENTION_DECLARATION,
    DocumentCategories.ABSENCE_CERTIFICATE,
    DocumentCategories.DRIVER_CARD,
    DocumentCategories.C4,
    DocumentCategories.CV,
    DocumentCategories.CODE_95,
    DocumentCategories.RESIDENCE_CARD,
    DocumentCategories.BURDEN_OF_PROOF_EMPLOYEE,
    DocumentCategories.CERTIFICATE_OF_GOOD_CONDUCT,
    DocumentCategories.A1_DECLARATION,
  ];
  return categoriesWithEmployeeDropdown.map(categoryId => Number(categoryId)).includes(type);
};

export const isValidDate = (d: any) => {
  return d instanceof Date && !isNaN(d);
};

export const isPeriodValid = (
  startDate: Date,
  endDate: Date,
  docType: string,
): { value: boolean; errorType: string } => {
  if (docType === DocumentCategories.INTENTION_DECLARATION || docType === DocumentCategories.CV) {
    return { value: true, errorType: '' };
  }

  if (!isValidDate(startDate) || !isValidDate(endDate)) {
    return { value: false, errorType: 'invalidPeriod' };
  }
  if (startDate && endDate) {
    const noErrorType = '';
    const millisecondsPerDay = 24 * 60 * 60 * 1000;
    const difference = (treatAsUTC(endDate).getTime() - treatAsUTC(startDate).getTime()) / millisecondsPerDay;
    const isInChronologicalOrder = difference >= 0;

    if (docType === DocumentCategories.ID) {
      const currentTime = new Date();
      const startDateString = typeof startDate === 'string' ? startDate : startDate.toDateString();
      const endDateString = typeof endDate === 'string' ? endDate : endDate.toDateString();
      const isValidPeriodForId = startDate < endDate && endDate > currentTime && startDateString !== endDateString;
      return { value: isValidPeriodForId, errorType: isValidPeriodForId ? noErrorType : 'invalidExpirationDate' };
    }

    if (docType !== DocumentCategories.STUDENT_INSCRIPTION_PROOF) {
      return { value: isInChronologicalOrder, errorType: isInChronologicalOrder ? noErrorType : 'invalidPeriod' };
    }

    const numberOfDaysInAYear = 365;
    const isMaximumOneYear = difference <= numberOfDaysInAYear;
    const validationForStudentInscription = isInChronologicalOrder && isMaximumOneYear;
    return {
      value: validationForStudentInscription,
      errorType: validationForStudentInscription ? noErrorType : 'invalidAcademicPeriod',
    };
  }
  return { value: false, errorType: 'required' };
};
